import React, { useEffect, useRef, useState } from 'react'

import {
  agGridContainerLongStyle,
  dateColDefs,
  defaultMultipleSelectionHeaderColumn,
} from '@/constants/ag-grid-scenario-builder.constant'
import { optimizationDefaultColDefs } from '@/constants/portfolioOptimization.constant'
import { IPortfolio } from '@/interfaces/portfolioOptimization.interface'
import { getAllPortfolios, getPortfolios } from '@/services/portfolioOptimization.service'
import { clearSelectedPortfolios } from '@/states/portfolioOptmization.slice'
import { showSnackbar } from '@/states/snackbar.slice'
import { portfolioOptimizationDefaultGridOptions } from '@/utils/grid-utils/colDefs'
import {
  ColDef,
  GridOptions,
  RowSelectedEvent,
  ValueGetterParams,
} from '@ag-grid-community/core'
import { AgGridReact } from '@ag-grid-community/react'
import { Box, Button } from '@mui/material'
import { useDispatch } from 'react-redux'

type PortfolioGridProps = {
  onSelectPortfolios: (portfolio: IPortfolio[]) => void
}

export const PortfoliosGrid: React.FC<PortfolioGridProps> = ({
  onSelectPortfolios,
}: PortfolioGridProps) => {
  // Grid Configuration
  const appDispatch = useDispatch()
  const [loading, setLoading] = useState(true)
  const [portfolios, setPortfolios] = useState<IPortfolio[]>([])
  const [showAllPortfolios, setShowAllPortfolios] = useState(false)
  const gridRef = useRef<AgGridReact>(null)

  const gridOptions: GridOptions = {
    ...portfolioOptimizationDefaultGridOptions,
    localeText: { dateFormatOoo: 'yyyy-mm-dd' },
    noRowsOverlayComponentParams: () => {
      return {
        noRowsMessage: 'No portfolios available.',
      }
    },
    rowSelection: 'multiple' as const,
    columnDefs: [
      {
        ...defaultMultipleSelectionHeaderColumn,
        suppressHeaderMenuButton: true,
        sortable: false,
        filter: false,
      },
      {
        ...{ ...optimizationDefaultColDefs, ...dateColDefs },
        headerName: 'Date',
        field: 'date',
        filter: 'agDateColumnFilter',
      },
      {
        ...optimizationDefaultColDefs,
        headerName: 'Portfolio Name/Benchmark Name',
        field: 'name',
        cellDataType: 'text',
        valueGetter: (params: ValueGetterParams) => {
          if (params?.data) {
            return `${params.data?.name}/${params.data?.benchmarkName}`
          }
          return ''
        },
      },
      {
        ...optimizationDefaultColDefs,
        headerName: 'Type',
        field: 'type',
        cellDataType: 'string',
      },
      {
        ...optimizationDefaultColDefs,
        headerName: 'Securities',
        field: 'numOfSecurities',
        cellDataType: 'number',
        filter: 'agNumberColumnFilter',
      },
      {
        ...optimizationDefaultColDefs,
        headerName: 'User',
        field: 'user',
        cellDataType: 'string',
      },
    ] as ColDef[],
    onRowSelected: (params: RowSelectedEvent) => {
      const nodes = params.api.getSelectedNodes()
      onSelectPortfolios(nodes?.map((node) => node?.data))
    },
  }

  // Fetch the portfolio list, either restricted to the last 6 months or all dates
  const loadPortfolios = async (allDates: boolean) => {
    setLoading(true)
    try {
      let response: IPortfolio[]
      if (allDates) {
        try {
          response = await getAllPortfolios()
        } catch (err) {
          setShowAllPortfolios(false)
          appDispatch(
            showSnackbar({
              message:
                'Unable to load all portfolios. Showing the last 6 months instead.',
              severity: 'warning',
            }),
          )
          response = await getPortfolios()
        }
      } else {
        response = await getPortfolios()
      }
      setPortfolios(response)
    } catch (err) {
      appDispatch(
        showSnackbar({
          message: `Failed to load portfolios`,
          severity: 'error',
        }),
      )
      setPortfolios([])
    } finally {
      setLoading(false)
    }
  }

  // Toggle between the last-6-months portfolio list and the full portfolio list
  const handleTogglePortfolioRange = () => {
    const nextShowAllPortfolios = !showAllPortfolios
    setShowAllPortfolios(nextShowAllPortfolios)
    loadPortfolios(nextShowAllPortfolios)
  }

  useEffect(() => {
    // Only clear the globally selected portfolios on initial load — a
    // committed selection (via "Use Selected Portfolio") should survive
    // toggling the date range afterward.
    loadPortfolios(showAllPortfolios).finally(() => {
      appDispatch(clearSelectedPortfolios())
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <>
      <Box display='flex' justifyContent='flex-end' mb={1}>
        <Button
          size='small'
          variant='outlined'
          onClick={handleTogglePortfolioRange}
        >
          {showAllPortfolios ? 'Show Last 6 Months' : 'Show All Portfolios'}
        </Button>
      </Box>
      <Box
        className='ag-theme-quartz select-portfolios-table'
        boxSizing='border-box'
        sx={{ ...agGridContainerLongStyle }}
      >
        <AgGridReact
          ref={gridRef}
          rowData={portfolios}
          gridOptions={gridOptions}
          data-cy='select-portfolios-ag-grid'
          loading={loading}
        ></AgGridReact>
      </Box>
    </>
  )
}
